"use client";

import React, { useRef, useState, useEffect, useCallback, ReactNode } from "react";

interface JtbdContentRowProps {
  title: string;
  subtitle?: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  cardWidth?: string; // Tailwind width class, e.g. "w-40 md:w-48"
  /** Optional badge rendered next to the title (e.g. a Free pill). */
  titleBadge?: ReactNode;
  children: ReactNode;
}

export default function JtbdContentRow({
  title,
  subtitle,
  viewAllHref,
  viewAllLabel = "View all →",
  cardWidth,
  titleBadge,
  children,
}: JtbdContentRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 8);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Initial check after layout
    const raf = requestAnimationFrame(() => checkScroll());
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll, children]);

  const scrollBy = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-800"
              style={{ fontFamily: "var(--font-merriweather)" }}
            >
              {title}
            </h2>
            {titleBadge}
          </div>
          {subtitle && <p className="text-gray-500 text-sm md:text-base mt-1">{subtitle}</p>}
        </div>
        {viewAllHref && (
          <a
            href={viewAllHref}
            className="text-primary font-semibold hover:underline whitespace-nowrap text-sm md:text-base"
          >
            {viewAllLabel}
          </a>
        )}
      </div>

      {/* Scroll container (no `group` here — card hover must be scoped per-card) */}
      <div className="relative">
        {/* Left arrow */}
        {showLeftArrow && (
          <button
            type="button"
            onClick={() => scrollBy("left")}
            aria-label="Scroll left"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all -ml-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Right arrow */}
        {showRightArrow && (
          <button
            type="button"
            onClick={() => scrollBy("right")}
            aria-label="Scroll right"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all -mr-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Scrollable row */}
        <div
          ref={scrollRef}
          className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {React.Children.map(children, (child) => (
            <div className={`${cardWidth || "w-40 md:w-48"} shrink-0 snap-start`}>
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}